import {
  AlertTriangle,
  ArrowLeftRight,
  Eye,
  EyeOff,
  Save,
  Settings,
  Trash2
} from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useNotification } from '../contexts/NotificationContext'
import { deleteGroup, transferGroupOwnership, updateGroup } from '../services/groupService'
import { AnimatedPresence } from './ui/animated-presence'
import { Button } from './ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card'
import { Input } from './ui/input'
import { Toggle } from './ui/toggle'

export default function GroupSettings({ 
  group, 
  members, 
  onGroupUpdated, 
  onGroupDeleted 
}) {
  const navigate = useNavigate()
  const notify = useNotification()
  
  const [name, setName] = useState(group?.name || '')
  const [description, setDescription] = useState(group?.description || '')
  const [isPrivate, setIsPrivate] = useState(group?.private || false)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteConfirmText, setDeleteConfirmText] = useState('')
  
  const [showTransferModal, setShowTransferModal] = useState(false)
  const [selectedNewOwner, setSelectedNewOwner] = useState(null)
  const [transferring, setTransferring] = useState(false)

  const hasChanges = 
    name !== group?.name || 
    description !== (group?.description || '') ||
    isPrivate !== (group?.private || false)

  const handleSave = async () => {
    if (!name.trim()) {
      notify.error('Group name is required')
      return
    }

    setSaving(true)
    try {
      await updateGroup({
        groupId: group.id,
        name: name.trim(),
        // Always send a string: '' clears the description server-side.
        // NULL would mean "keep the existing value" and the edit reverts (DUB-195).
        description: description.trim(),
        isPublic: group?.is_public ?? true,
        isPrivate
      })

      notify.success('Group settings saved')
      if (onGroupUpdated) {
        onGroupUpdated({
          ...group,
          name: name.trim(),
          description: description.trim() || null,
          private: isPrivate
        })
      }
    } catch (err) {
      console.error('Error updating group:', err)
      notify.error(err.message || 'Failed to update group')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (deleteConfirmText !== group.name) {
      notify.error('Please type the group name exactly to confirm deletion')
      return
    }

    setDeleting(true)
    try {
      await deleteGroup({ groupId: group.id })

      notify.success('Group deleted successfully')
      if (onGroupDeleted) {
        onGroupDeleted()
      } else {
        navigate('/groups')
      }
    } catch (err) {
      console.error('Error deleting group:', err)
      notify.error(err.message || 'Failed to delete group')
    } finally {
      setDeleting(false)
    }
  }

  const handleTransferOwnership = async () => {
    if (!selectedNewOwner) {
      notify.error('Please select a member to transfer ownership to')
      return
    }

    setTransferring(true)
    try {
      await transferGroupOwnership({
        groupId: group.id,
        newOwnerId: selectedNewOwner
      })

      notify.success('Ownership transferred successfully')
      setShowTransferModal(false)
      setSelectedNewOwner(null)
      if (onGroupUpdated) {
        onGroupUpdated({ ...group })
      }
    } catch (err) {
      console.error('Error transferring ownership:', err)
      notify.error(err.message || 'Failed to transfer ownership')
    } finally {
      setTransferring(false)
    }
  }

  const getInitials = (name) => {
    if (!name) return '?'
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
  }

  const nonOwnerMembers = members?.filter(m => m.role !== 'owner') || []

  return (
    <div className="space-y-6">
      {/* General Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            General Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Group Name */}
          <div>
            <label className="text-sm font-medium mb-2 block">Group Name *</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter group name"
              maxLength={100}
            />
          </div>

          {/* Description */}
          <div>
            <label className="text-sm font-medium mb-2 block">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe your group..."
              className="w-full min-h-[100px] px-3 py-2 border rounded-md bg-background resize-none focus:outline-none focus:ring-2 focus:ring-ring"
              maxLength={500}
            />
            <p className="text-xs text-muted-foreground mt-1">
              {description.length}/500 characters
            </p>
          </div>

          {/* Visibility: the label names the current state, the subtext names what the toggle does */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex items-center gap-3">
              {isPrivate ? (
                <EyeOff className="h-5 w-5 text-muted-foreground" />
              ) : (
                <Eye className="h-5 w-5 text-primary" />
              )}
              <div>
                <p>
                  <span className="font-semibold">{isPrivate ? 'Private:' : 'Public:'}</span>{' '}
                  {isPrivate ? 'Invitation link required' : 'Anyone can join'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {isPrivate ? 'Toggle to make it public' : 'Toggle to make it private'}
                </p>
              </div>
            </div>
            <Toggle
              checked={isPrivate}
              onCheckedChange={(checked) => setIsPrivate(checked)}
            />
          </div>

          {/* Save Button */}
          <AnimatedPresence show={hasChanges && !!name.trim()}>
            <div className="flex justify-end pt-4 border-t">
              <Button 
                variant="primaryNeon"
                onClick={handleSave} 
                disabled={saving}
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </AnimatedPresence>
        </CardContent>
      </Card>

      {/* Transfer Ownership */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ArrowLeftRight className="h-5 w-5" />
            Transfer Ownership
          </CardTitle>
          <CardDescription>
            Transfer group ownership to another member
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            Transferring ownership will make another member the owner and demote you to a regular member.
            This action cannot be undone by you.
          </p>
          <Button 
            variant="outline" 
            onClick={() => setShowTransferModal(true)}
            disabled={nonOwnerMembers.length === 0}
          >
            <ArrowLeftRight className="h-4 w-4 mr-2" />
            Transfer Ownership
          </Button>
          {nonOwnerMembers.length === 0 && (
            <p className="text-xs text-muted-foreground mt-2">
              No other members to transfer ownership to
            </p>
          )}
        </CardContent>
      </Card>

      {/* Danger Zone */}
      <Card className="border-destructive/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            Danger Zone
          </CardTitle>
          <CardDescription>
            Irreversible actions that affect your group
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!showDeleteConfirm ? (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 border border-destructive/30 rounded-lg">
              <div>
                <p className="font-medium">Delete Group</p>
                <p className="text-sm text-muted-foreground">
                  Permanently delete this group and all its data
                </p>
              </div>
              <Button 
                variant="destructive" 
                onClick={() => setShowDeleteConfirm(true)}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Group
              </Button>
            </div>
          ) : (
            <div className="p-4 border border-destructive rounded-lg bg-destructive/5">
              <div className="flex items-start gap-3 mb-4">
                <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-destructive">Are you absolutely sure?</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    This will permanently delete <strong>{group.name}</strong> and all associated data including:
                  </p>
                  <ul className="text-sm text-muted-foreground mt-2 list-disc list-inside space-y-1">
                    <li>All member associations</li>
                    <li>All bracket associations</li>
                    <li>All prizes configured for this group</li>
                    <li>All leaderboard data for this group</li>
                  </ul>
                </div>
              </div>
              
              <div className="mb-4">
                <label className="text-sm font-medium mb-2 block">
                  Type <strong>{group.name}</strong> to confirm:
                </label>
                <Input
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder={group.name}
                  className="border-destructive/50 focus:border-destructive"
                />
              </div>
              
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowDeleteConfirm(false)
                    setDeleteConfirmText('')
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={deleteConfirmText !== group.name || deleting}
                >
                  {deleting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Group Permanently
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Transfer Ownership Modal */}
      {showTransferModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ArrowLeftRight className="h-5 w-5" />
                Transfer Ownership
              </CardTitle>
              <CardDescription>
                Select a member to become the new owner
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-64 overflow-y-auto mb-4">
                {nonOwnerMembers.map((member) => (
                  <button
                    key={member.user_id}
                    onClick={() => setSelectedNewOwner(member.user_id)}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                      selectedNewOwner === member.user_id
                        ? 'border-primary bg-primary/5 ring-2 ring-primary'
                        : 'hover:bg-muted/60'
                    }`}
                  >
                    <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center text-sm font-semibold">
                      {getInitials(member.full_name)}
                    </div>
                    <div className="text-left">
                      <p className="font-medium">{member.full_name || 'Unknown'}</p>
                      <p className="text-xs text-muted-foreground">Member</p>
                    </div>
                  </button>
                ))}
              </div>
              
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowTransferModal(false)
                    setSelectedNewOwner(null)
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  variant="primaryNeon"
                  onClick={handleTransferOwnership}
                  disabled={!selectedNewOwner || transferring}
                  className="flex-1"
                >
                  {transferring ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Transferring...
                    </>
                  ) : (
                    'Transfer Ownership'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}
